/**
 * frontend/App.js
 * SoundDAO — Root application router.
 *
 * Routing is path-based using react-router-dom v6.
 * Auth state is managed by useAuth() which wraps supabase.auth.
 *
 * Reactive routing logic (evaluated every render):
 *   loading                           → LoadingScreen (initial check + post-SIGNED_IN profile fetch)
 *   !session                          → PublicLanding at /, AuthScreen at /login
 *   session && !user                  → /onboarding (new user, 404 from profile API)
 *   session && user && !user.persona  → /onboarding (authenticated, not yet onboarded)
 *   session && user && user.persona   → /dashboard/:persona
 *
 * useAuth owns navigation on SIGNED_IN — after fetching the profile it calls
 * navigate() directly.  /auth/callback (pages/AuthCallback) is a spinner while
 * Supabase parses the hash and fires SIGNED_IN (implicit flow +
 * detectSessionInUrl: true); it only shows an error card when the redirect
 * carries an error or nothing has happened after 15 s.
 *
 * Routes:
 *   /                    → PublicLanding (not logged in) OR redirect to dashboard
 *   /login               → AuthScreen (not logged in) OR redirect to /
 *   /auth/callback       → AuthCallback (spinner; error card on an expired/used link or after 15 s)
 *   /onboarding          → PersonaSelection (new user, no persona yet)
 *   /reward              → RewardMoment (+100 SOUND celebration)
 *   /dashboard/player    → PlayerDashboard
 *   /dashboard/creator   → CreatorDashboard
 *   /dashboard/collector → CollectorDashboard
 *   /settings            → Settings
 *   /creator-onboarding  → CreatorOnboarding (auth + creator persona required)
 *   /creator/:creator_id → CreatorProfile (public)
 *   /creators            → CreatorDiscovery (public)
 *   /leaderboard         → Leaderboard (public)
 *   /artist-pick         → ArtistPickStep (player onboarding step)
 *   /join/artist/:code   → ArtistInviteLanding (public)
 *   /dig                 → Dig (Sprint 6A: search the Internet Archive)
 *   /dig/item/:id        → DigItem (tracks, ▶, + Playlist)
 *   /playlists           → MyPlaylists (Built here + Linked)
 *   /playlists/:id       → PlaylistEdit
 *   /playlist/:slug      → PublicPlaylist (public, playable signed-out)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';

import { useAuth }           from './hooks/useAuth';
import AuthScreen            from './AuthScreen';
import PersonaOnboarding     from './PersonaOnboarding';
import PublicLanding        from './pages/PublicLanding';
import RewardMoment          from './RewardMoment';
import Settings              from './Settings';
import CreatorOnboarding     from './CreatorOnboarding';
import CreatorProfile        from './pages/CreatorProfile';
import CreatorDiscovery      from './pages/CreatorDiscovery';
import Leaderboard           from './pages/Leaderboard';
import PlayerDashboard       from './dashboards/PlayerDashboard';
import CreatorDashboard      from './dashboards/CreatorDashboard';
import CollectorDashboard    from './dashboards/CollectorDashboard';
import ArtistInviteLanding   from './ArtistInviteLanding';
import InviteLanding         from './pages/InviteLanding';
import AuthCallback          from './pages/AuthCallback';
import ArtistPickStep        from './onboarding/ArtistPickStep';
import RexTab                from './tabs/RexTab';
import IvyTab                from './tabs/IvyTab';
import AtlasTab              from './tabs/AtlasTab';
import RankingsTab           from './tabs/RankingsTab';
import DiscoverTab           from './tabs/DiscoverTab';
import BottomNav, { shouldShowBottomNav } from './components/BottomNav';
import { PlayerProvider }    from './player/PlayerContext';
import MiniPlayer            from './player/MiniPlayer';
import Dig                   from './pages/Dig';
import DigItem               from './pages/DigItem';
import MyPlaylists           from './pages/MyPlaylists';
import PlaylistEdit          from './pages/PlaylistEdit';
import PublicPlaylist        from './pages/PublicPlaylist';
import ClaimPage             from './pages/ClaimPage';
import ClaimVerify           from './pages/ClaimVerify';
import CopyrightPolicy       from './pages/CopyrightPolicy';
import { TermsOfService, PrivacyPolicy } from './pages/LegalPages';
import { getPendingClaim }   from './lib/archiveApi';
import JackpotOverlay        from './components/JackpotOverlay';
import VaultWidget           from './components/VaultWidget';
import BreakingArtistOverlay from './components/BreakingArtistOverlay';
import { supabase }          from './lib/supabaseClient';
import { authFetch }         from './lib/authFetch';
import { playRewardSound }   from './utils/rewardSounds';
import { Analytics }         from '@vercel/analytics/react';
import { takeRecruitIntent } from './lib/userStorage';

import './App.css';
import BrandMark from './components/BrandMark';
import { prefetchCreators } from './lib/creatorsList';

// Discover opened directly (or refreshed): start loading the public artist list now, in
// parallel with the signed-in profile, instead of after it.
if (typeof window !== 'undefined' && /^\/(player\/discover|creators)\/?$/.test(window.location.pathname)) {
  prefetchCreators();
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// ── Public paths — rendered without waiting for auth loading to resolve ────────
// These paths are fully accessible with no session and must never redirect
// logged-in users away from the content.
// /auth/callback must be public so the authPending gate never blocks it —
// it renders AuthCallback and useAuth navigates away once SIGNED_IN fires.
// Sprint 6-Zero M3: '/' is now the public front door (PublicLanding) and the
// auth screen has moved to '/login'. Both must be reachable signed-out.
const PUBLIC_PATHS = new Set(['/', '/login', '/creators', '/leaderboard', '/player/rankings', '/auth/callback', '/legal/copyright', '/legal/terms', '/legal/privacy']);
function isPublicPath(pathname) {
  if (PUBLIC_PATHS.has(pathname)) return true;
  // /creator/ivy is Ivy's (auth-only) home, NOT the public /creator/:id profile. Treating it
  // as public rendered its route before auth resolved, and the route's `!session` branch
  // bounced the signed-in visitor to / and on to the dashboard.
  if (pathname === '/creator/ivy' || pathname === '/creator/ivy/') return false;
  // /creator/:id  and  /invite/:referral_code  and /join/artist/:invite_code
  if (pathname.startsWith('/creator/')) return true;
  if (pathname.startsWith('/invite/'))  return true;
  if (pathname.startsWith('/join/'))    return true;
  // Sprint 6A: shared built playlists, playable signed-out. ('/playlists…' is NOT public.)
  if (pathname.startsWith('/playlist/')) return true;
  // Sprint 6A Prompt 6: the claim landing page is public; its /verify step is not.
  if (pathname.startsWith('/claim/') && !/\/verify\/?$/.test(pathname)) return true;
  return false;
}

// ── Loading splash ─────────────────────────────────────────────────────────────
function LoadingScreen({ message } = {}) {
  return (
    <div className="app-loading">
      <div className="app-loading-logo"><BrandMark /></div>
      <div className="app-loading-spinner" />
      {message && <p className="app-loading-message">{message}</p>}
    </div>
  );
}

// ── Smart root redirect ────────────────────────────────────────────────────────
// Routing is purely reactive to auth state from useAuth.
// loading is handled by the gate in AppInner before this ever renders.
//
// Sprint 6A — Players always land on /player/rex (Rex is home).
// Creators/Collectors land on their dashboard.
function RootRedirect({ session, user }) {
  // Sprint 6-Zero M3: signed-out visitors get the public marketing page, not the
  // login card. The auth screen lives at /login and PublicLanding links to it.
  if (!session)        return <PublicLanding />;
  if (!user?.persona)  return <Navigate to="/onboarding" replace />;
  if (user.persona === 'player') {
    return <Navigate to="/player/rex" replace />;
  }
  return <Navigate to={`/dashboard/${user.persona}`} replace />;
}

// ── Dashboard wrapper ─────────────────────────────────────────────────────────
// Injects user; provides settings navigation.
// It used to fetch /api/rewards/balance here too, but each dashboard reads its `balance`
// prop only as useState's initial value (0 at mount, before this fetch returned) and loads
// the balance itself — so that request was a duplicate whose result was never shown.
function DashboardWrapper({ persona, session, user, refreshUser }) {
  const navigate = useNavigate();
  const commonProps = { user, balance: 0, session, onSettings: () => navigate('/settings') };

  if (persona === 'player')    return <PlayerDashboard    {...commonProps} />;
  if (persona === 'creator')   return <CreatorDashboard   {...commonProps} />;
  if (persona === 'collector') return <CollectorDashboard {...commonProps} />;
  return <Navigate to="/" replace />;
}

// ── App inner (uses router hooks — must be inside BrowserRouter) ──────────────
function AppInner() {
  const { session, user, loading, seedUser, refreshUser } = useAuth();
  // Effects below are keyed on the signed-in user's id (and persona), never on the session
  // object: a token refresh must not re-run them. Code that needs the token gets the current
  // one through authFetch / the supabase client at call time.
  const userId = session?.user?.id || null;
  const navigate = useNavigate();
  const location = useLocation();

  // Pending reward: set by PersonaSelection, consumed by RewardMoment
  const [pendingReward, setPendingReward] = useState(null);

  // Artist pick step: shown for players after the reward moment
  const [showArtistPick, setShowArtistPick] = useState(false);

  // Login reward: shown once per session after auth resolves
  // { tier, amount } — null means no active overlay
  const [loginReward, setLoginReward] = useState(null);

  // Sprint 5A — Vault unlock overlay state
  const [vaultUnlockData, setVaultUnlockData] = useState(null);

  // Punch-list item 3 — does this creator have a completed `creators` row?
  // null = not checked yet, true/false = answered. BottomNav only redirects to
  // onboarding on an explicit false, so a slow check never misroutes a real
  // creator to the onboarding wizard.
  const [hasCreatorProfile, setHasCreatorProfile] = useState(null);

  useEffect(() => {
    if (!userId || user?.persona !== 'creator') { setHasCreatorProfile(null); return; }
    let cancelled = false;
    (async () => {
      // Scoped to THIS user's row only.
      const { data, error } = await supabase
        .from('creators')
        .select('user_id, artist_name')
        .eq('user_id', userId)
        .maybeSingle();
      if (cancelled) return;
      if (error) { setHasCreatorProfile(null); return; }   // unknown, do not redirect
      setHasCreatorProfile(Boolean(data && data.artist_name));
    })();
    return () => { cancelled = true; };
  }, [userId, user?.persona]);

  // Sprint 5A — Breaking artist overlay state
  const [breakingNotif, setBreakingNotif] = useState(null);  // { notification, creatorName, isFirstTime }

  // ── Daily login reward — fire once per browser session ───────────────────
  // Fires as soon as the auth session is confirmed — in parallel with the
  // profile fetch — so there's no sequential delay waiting for the user row.
  // The backend safely returns 404 for users who haven't yet completed
  // onboarding (caught silently). Gated by sessionStorage to fire exactly once.
  React.useEffect(() => {
    // A different user (or sign-out): never show the previous user's reward/vault overlay.
    setLoginReward(null);
    setVaultUnlockData(null);
    if (!userId) return;
    // Per user: userStorage clears this flag on sign-out and when another user signs in.
    if (sessionStorage.getItem('sounddao_login_reward_claimed')) return;
    sessionStorage.setItem('sounddao_login_reward_claimed', 'true'); // gate immediately

    // Prompt 1.5: the backend takes the user from the access token.
    authFetch(`${API_BASE}/api/rewards/login`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({}),
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const { tier, reward, vaultUnlocked, vaultUnlockAmount, vaultBalance, vaultCycleLogins } = data;

        // ── Sprint 6-Zero M6 item 2 — THE VAULT REGRESSION ────────────────────
        // This block used to sit below an early return:
        //     if (!data || data.reward === 0 || data.already_claimed) return;
        // so a vault unlock was DISCARDED whenever the daily draw happened to pay
        // nothing — which was 60% of draws under the old odds — or whenever the
        // 24h cooldown had not elapsed. The overlay fired only when an unlock
        // coincided with a winning draw, which is why it worked 4 times and then
        // appeared to stop. The vault is checked FIRST now, unconditionally.
        if (vaultUnlocked && vaultUnlockAmount) {
          setVaultUnlockData({
            vaultUnlockAmount,
            vaultBalance: vaultBalance || 0,
            // Vault CYCLE logins, not the login streak — the overlay reads
            // "N days of loyalty" and used to show the wrong number entirely.
            cycleLoginCount: vaultCycleLogins || 0,
          });
        }

        if (data.reward === 0 || data.already_claimed) return;
        playRewardSound(tier);
        if (tier !== 'nothing') {
          setLoginReward({ tier, amount: reward });
        }
      })
      .catch(() => {}); // non-fatal
  }, [userId]);

  // ── Sprint 5A — Listen for breaking_artist notifications ─────────────────
  // Once per signed-in user: one unread check and one Realtime channel, torn down on sign-out
  // or user change (a token refresh no longer re-subscribes).
  useEffect(() => {
    setBreakingNotif(null);   // never carry the previous user's overlay over
    if (!userId) return;
    let cancelled = false;

    // Check for existing unread breaking_artist notifications on load
    supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .eq('type', 'breaking_artist')
      .eq('read', false)
      .order('created_at', { ascending: false })
      .limit(1)
      .then(({ data }) => {
        if (cancelled) return;
        if (data && data.length > 0) {
          const notif = data[0];
          // Check if this is first time (no prior read breaking_artist for same creator)
          supabase
            .from('notifications')
            .select('id')
            .eq('user_id', userId)
            .eq('type', 'breaking_artist')
            .eq('creator_id', notif.creator_id)
            .eq('read', true)
            .limit(1)
            .then(({ data: priorData }) => {
              if (cancelled) return;
              const isFirst = !priorData || priorData.length === 0;
              setBreakingNotif({
                notification: notif,
                creatorName: notif.message?.match(/^(.+?) just went HOT/)?.[1] || 'Artist',
                isFirstTime: isFirst,
              });
            });
        }
      })
      .catch(() => {});

    // Real-time listener for new breaking_artist notifications
    const channel = supabase
      .channel(`breaking-artist-${userId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
        (payload) => {
          const notif = payload.new;
          if (notif.type !== 'breaking_artist') return;
          setBreakingNotif({
            notification: notif,
            creatorName: notif.message?.match(/^(.+?) just went HOT/)?.[1] || 'Artist',
            isFirstTime: true,
          });
        }
      )
      .subscribe();

    return () => { cancelled = true; supabase.removeChannel(channel); };
  }, [userId]);

  // ── handlePersonaConfirmed ────────────────────────────────────────────────
  // Called by PersonaSelection once POST /api/users/onboard succeeds.
  // The API response already contains the freshly-created user row — seed it
  // directly instead of firing a redundant GET /api/users/:id.
  const handlePersonaConfirmed = useCallback(({ user: u, persona, sound_awarded, agent }) => {
    seedUser(u);
    setPendingReward({ persona, sound_awarded, agent });
    navigate('/reward', { replace: true });
  }, [navigate, seedUser]);

  const handleRewardComplete = useCallback(() => {
    const persona = pendingReward?.persona || user?.persona;
    setPendingReward(null);
    // Mark this user as a returning visitor so future root visits land on feed
    localStorage.setItem('sounddao_returning', 'true');
    // New creators go through the profile setup wizard before hitting the dashboard
    if (persona === 'creator') {
      navigate('/creator-onboarding', { replace: true });
    } else if (persona === 'player') {
      // Players see the Artist Pick step before reaching their dashboard
      setShowArtistPick(true);
      navigate('/artist-pick', { replace: true });
    } else {
      navigate(`/dashboard/${persona}`, { replace: true });
    }
  }, [navigate, pendingReward, user?.persona]);

  // Called when the ArtistPickStep completes (recruited, nominated, or skipped)
  const handleArtistPickComplete = useCallback((result) => {
    setShowArtistPick(false);
    // If they recruited an artist, store the name for Rex's opening message
    if (result?.recruited) {
      localStorage.setItem('sounddao_recruited_artist', result.recruited);
    }
    navigate('/player/rex', { replace: true });
  }, [navigate]);

  // ── Post-login pending recruit action (Task 2.5) ─────────────────────────────
  // If a non-logged-in Player clicked "Recruit this creator" on a creator profile
  // and was sent to AuthScreen, InviteLanding stores sounddao_recruit_creator_id.
  // After login we fire the recruit award automatically, then clear the key.
  React.useEffect(() => {
    if (!userId || user?.persona !== 'player') return;
    // Sprint 6A.1: read-and-remove; null when older than 24 h (or cleared on sign-out / user change).
    const pendingCreatorId = takeRecruitIntent();
    if (!pendingCreatorId) return;

    // Prompt 1.5: recruits are verified and paid server-side; the recruiter is
    // the token holder.
    authFetch(`${API_BASE}/api/creators/${encodeURIComponent(pendingCreatorId)}/recruit`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ source: 'invite_flow' }),
    }).catch(err => console.warn('[App] pending recruit award failed silently:', err));
  }, [userId, user?.persona]);

  // ── Sprint 6A Prompt 6 — pending artist claim ─────────────────────────────────
  // A band that tapped "Claim your profile" signed-out carries the artist id through the
  // magic link in localStorage. Once they are a creator with a profile, take them to the
  // Verify step (once per browser session, and never off a claim / onboarding page).
  React.useEffect(() => {
    const claimId = getPendingClaim();
    if (!claimId || !userId || user?.persona !== 'creator' || !hasCreatorProfile) return;
    const p = location.pathname;
    if (p.startsWith('/claim/') || ['/creator-onboarding', '/onboarding', '/reward'].includes(p)) return;
    if (sessionStorage.getItem('sounddao_claim_redirected') === claimId) return;
    sessionStorage.setItem('sounddao_claim_redirected', claimId);
    navigate(`/claim/${claimId}/verify`, { replace: true });
  }, [userId, user?.persona, hasCreatorProfile, location.pathname, navigate]);

  // ── Loading gate ──────────────────────────────────────────────────────────────
  // Block rendering of protected routes while:
  //   (a) loading=true — initial session check or post-SIGNED_IN profile fetch
  //   (b) session is set but user===undefined — profile fetch is still in flight
  //       (the undefined/null/object distinction in useAuth.user means:
  //         undefined = fetch not yet run / in-flight
  //         null      = confirmed no profile (new user)
  //         object    = loaded profile)
  //
  // Without check (b), if getSession() returns null before the token exchange
  // completes and then SIGNED_IN fires, there is a brief window where loading=false
  // but session is set and user is still undefined.  Without this gate, RootRedirect
  // would see session && !user?.persona and send the user to /onboarding.
  const authPending = loading || (session !== null && user === undefined);
  if (authPending && !isPublicPath(location.pathname)) return <LoadingScreen />;

  return (
    <>
    {/* ── Bottom tab bar — shown on mobile when session is active ── */}
    {session && user?.persona && (
      <BottomNav user={user} hasCreatorProfile={hasCreatorProfile} />
    )}

    {/* ── Sprint 6A — persistent mini player (one app-level <audio>) ── */}
    <MiniPlayer withNav={Boolean(session && user?.persona && shouldShowBottomNav(location.pathname))} />

    {/* ── Login reward overlay ── */}
    {loginReward && (
      loginReward.tier === 'big_break'
        ? <JackpotOverlay
            amount={loginReward.amount}
            referralCode={user?.referral_code || null}
            onDismiss={() => setLoginReward(null)}
          />
        : <RewardMoment
            soundAwarded={loginReward.amount}
            onComplete={() => setLoginReward(null)}
          />
    )}

    {/* ── Sprint 5A — Vault unlock overlay ── */}
    {vaultUnlockData && !loginReward && (
      <VaultWidget
        vaultBalance={vaultUnlockData.vaultBalance}
        lockedUntil={null}
        cycleLoginCount={vaultUnlockData.cycleLoginCount}
        vaultUnlocked={true}
        vaultUnlockAmount={vaultUnlockData.vaultUnlockAmount}
        onDismiss={() => setVaultUnlockData(null)}
      />
    )}

    {/* ── Sprint 5A — Breaking artist overlay ── */}
    {breakingNotif && !loginReward && !vaultUnlockData && (
      <BreakingArtistOverlay
        notification={breakingNotif.notification}
        creatorName={breakingNotif.creatorName}
        isFirstTime={breakingNotif.isFirstTime}
        onDismiss={() => {
          // Mark notification as read
          if (breakingNotif.notification?.id) {
            supabase
              .from('notifications')
              .update({ read: true })
              .eq('id', breakingNotif.notification.id)
              .then(() => {});
          }
          setBreakingNotif(null);
        }}
      />
    )}

    <Routes>
      {/* Root — smart redirect based on auth state */}
      <Route path="/"
        element={<RootRedirect session={session} user={user} />}
      />

      {/* Magic link callback — static loading screen.
          Supabase parses the URL hash automatically (implicit flow +
          detectSessionInUrl: true) and fires SIGNED_IN.  useAuth's
          SIGNED_IN handler fetches the profile then navigates to
          /dashboard/:persona or /onboarding.  No JS needed here. */}
      <Route path="/auth/callback"
        element={<AuthCallback />}
      />

      {/* Onboarding — requires session */}
      <Route path="/onboarding"
        element={
          session
            ? <PersonaOnboarding session={session} onConfirmed={handlePersonaConfirmed} />
            : <Navigate to="/" replace />
        }
      />

      {/* Reward moment */}
      <Route path="/reward"
        element={
          pendingReward
            ? <RewardMoment
                persona={pendingReward.persona}
                soundAwarded={pendingReward.sound_awarded}
                agent={pendingReward.agent}
                onComplete={handleRewardComplete}
              />
            : <Navigate to={
                session
                  ? user?.persona ? `/dashboard/${user.persona}` : '/onboarding'
                  : '/'
              } replace />
        }
      />

      {/* ── Sprint 6B follow-up — Agent home-tab routes ─────────────────
          /creator/ivy and /collector/atlas are declared BEFORE the
          /creator/:creator_id public profile route so React Router v6
          matches the specific path first.  Each is gated to its persona
          so a player/collector that lands on /creator/ivy bounces to
          their own home, and vice versa. */}
      <Route path="/creator/ivy"
        element={
          !session
            ? <Navigate to="/" replace />
            : user?.persona && user.persona !== 'creator'
              ? <Navigate to={`/dashboard/${user.persona}`} replace />
              : <IvyTab user={user} session={session} />
        }
      />
      <Route path="/collector/atlas"
        element={
          !session
            ? <Navigate to="/" replace />
            : user?.persona && user.persona !== 'collector'
              ? <Navigate to={`/dashboard/${user.persona}`} replace />
              : <AtlasTab user={user} session={session} />
        }
      />

      {/* ── Sprint 6A — Player tab routes ─────────────────────────────── */}
      <Route path="/player/rex"
        element={
          !session ? <Navigate to="/" replace />
            : <RexTab user={user} session={session} balance={0} />
        }
      />
      <Route path="/player/dashboard"
        element={
          !session ? <Navigate to="/" replace />
            : <DashboardWrapper persona="player" session={session} user={user} refreshUser={refreshUser} />
        }
      />
      <Route path="/player/rankings"
        element={<RankingsTab session={session} user={user} />}
      />
      <Route path="/player/discover"
        element={
          !session ? <Navigate to="/" replace />
            : <DiscoverTab session={session} user={user} />
        }
      />

      {/* Dashboards (legacy routes + creator/collector) */}
      {['player', 'creator', 'collector'].map(persona => (
        <Route key={persona} path={`/dashboard/${persona}`}
          element={
            !session
              ? <Navigate to="/" replace />
              : (user?.persona && user.persona !== persona)
                ? <Navigate to={`/dashboard/${user.persona}`} replace />
                : <DashboardWrapper
                    persona={persona}
                    session={session}
                    user={user}
                    refreshUser={refreshUser}
                  />
          }
        />
      ))}

      {/* Settings */}
      <Route path="/settings"
        element={
          session
            ? <Settings
                session={session}
                user={user}
                onUserUpdated={refreshUser}
                onBack={() => navigate(-1)}
              />
            : <Navigate to="/" replace />
        }
      />

      {/* Creator onboarding wizard — requires session + creator persona */}
      <Route path="/creator-onboarding"
        element={
          !session
            ? <Navigate to="/" replace />
            : <CreatorOnboarding
                session={session}
                user={user}
                prefill={user}
                onComplete={async () => {
                  // Check if this creator arrived via an artist invite link
                  const inviteCode = localStorage.getItem('artist_invite_code');
                  if (inviteCode && session?.user?.id) {
                    localStorage.removeItem('artist_invite_code');
                    try {
                      // The new creator is the token holder (Prompt 1.5).
                      await authFetch(`${API_BASE}/api/nominations/complete`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ invite_code: inviteCode }),
                      });
                    } catch (err) {
                      console.warn('[App] nomination complete failed silently:', err);
                    }
                  }
                  const claimId = getPendingClaim();
                  navigate(claimId ? `/claim/${claimId}/verify` : '/dashboard/creator', { replace: true });
                }}
                onSkip={() => {
                  const claimId = getPendingClaim();
                  navigate(claimId ? `/claim/${claimId}/verify` : '/dashboard/creator', { replace: true });
                }}
              />
        }
      />

      {/* Public creator profile — no auth required */}
      <Route path="/creator/:creator_id"
        element={<CreatorProfile session={session} user={user} />}
      />

      {/* Creator discovery grid — no auth required */}
      <Route path="/creators"
        element={<CreatorDiscovery session={session} showDigEntry={Boolean(session)} />}
      />

      {/* Global leaderboard — no auth required (Sprint 2 M3) */}
      <Route path="/leaderboard"
        element={<Leaderboard session={session} user={user} />}
      />

      {/* Artist Pick — onboarding step for Players */}
      <Route path="/artist-pick"
        element={
          showArtistPick && session
            ? <ArtistPickStep
                userId={session.user.id}
                onComplete={handleArtistPickComplete}
              />
            : <Navigate to={session ? (user?.persona ? `/dashboard/${user.persona}` : '/onboarding') : '/'} replace />
        }
      />

      {/* Artist invite landing — public */}
      <Route path="/join/artist/:invite_code"
        element={<ArtistInviteLanding />}
      />

      {/* Invite / referral landing — public, Sprint 2 M2 */}
      <Route path="/invite/:referral_code"
        element={<InviteLanding session={session} />}
      />

      {/* ── Sprint 6A — Dig the Archive + playlists ─────────────────────── */}
      <Route path="/dig"
        element={!session ? <Navigate to="/login" replace /> : <Dig />}
      />
      <Route path="/dig/item/:identifier"
        element={!session ? <Navigate to="/login" replace /> : <DigItem />}
      />
      <Route path="/playlists"
        element={!session ? <Navigate to="/login" replace /> : <MyPlaylists session={session} user={user} />}
      />
      <Route path="/playlists/:id"
        element={!session ? <Navigate to="/login" replace /> : <PlaylistEdit />}
      />
      {/* Sprint 6A Prompt 6 — artist claims. The landing page is public; verify needs a session. */}
      <Route path="/claim/:artistId"
        element={<ClaimPage session={session} user={user} />}
      />
      <Route path="/claim/:artistId/verify"
        element={!session ? <Navigate to="/login" replace /> : <ClaimVerify user={user} />}
      />
      {/* Public shared playlist — no auth; MUST stay above the catch-all. */}
      <Route path="/playlist/:slug"
        element={<PublicPlaylist session={session} />}
      />

      {/* Sign-in — public. Signed-in visitors are bounced to their destination.
          MUST stay above the catch-all below, which sends * to /. */}
      <Route path="/login"
        element={
          !session
            ? <AuthScreen />
            : <Navigate to="/" replace />
        }
      />

      {/* Copyright / DMCA policy — public, readable signed-in or out. Above the catch-all. */}
      <Route path="/legal/copyright" element={<CopyrightPolicy />} />
      <Route path="/legal/terms"     element={<TermsOfService />} />
      <Route path="/legal/privacy"   element={<PrivacyPolicy />} />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

// ── Root export ───────────────────────────────────────────────────────────────
export default function App() {
  return (
    <BrowserRouter>
      <PlayerProvider>
        <AppInner />
      </PlayerProvider>
      <Analytics />
    </BrowserRouter>
  );
}
